import { Link } from '@tanstack/react-router';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { DateInput } from '@/components/date-input';
import { Button } from '@/components/ui/button';
import { displayDate, todayIso } from '@/lib/dates';
import { VAT_PRESETS, vatPresetRange } from '@/lib/vat';

/**
 * Report page pattern (document 05, pattern 6) for the VAT reports: period controls, toolbar
 * with print and export, centred header, accrual footer.
 */
export function VatReportShell({
  title,
  orgName,
  trn,
  from,
  to,
  onRun,
  onExport,
  generatedAt,
  note,
  children,
}: {
  title: string;
  orgName?: string | undefined;
  trn?: string | null | undefined;
  from: string;
  to: string;
  onRun: (range: { from: string; to: string }) => void;
  onExport?: (() => void) | undefined;
  generatedAt?: string | undefined;
  note?: ReactNode;
  children: ReactNode;
}) {
  const [draft, setDraft] = useState({ from, to });
  useEffect(() => setDraft({ from, to }), [from, to]);
  return (
    <>
      <Link
        to="/reports"
        className="mb-2 inline-flex items-center gap-1 text-sm text-primary hover:underline print:hidden"
      >
        <ArrowLeft className="size-4" /> Back to report list
      </Link>
      <h1 className="mb-4 font-display text-[1.7rem] font-medium tracking-[-0.01em]">
        {title} report
      </h1>
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border bg-card p-4 print:hidden">
        <label className="flex flex-col gap-1 text-sm font-medium">
          Report period
          <select
            className="h-9 rounded-md border border-input bg-card px-3 text-sm"
            value=""
            onChange={(e) => {
              const r = vatPresetRange(e.target.value, todayIso());
              if (r) setDraft(r);
            }}
          >
            <option value="">Custom</option>
            {VAT_PRESETS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm font-medium">
          From
          <DateInput
            className="w-[150px]"
            value={draft.from}
            onChange={(v) => setDraft((d) => ({ ...d, from: v }))}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm font-medium">
          To
          <DateInput
            className="w-[150px]"
            value={draft.to}
            onChange={(v) => setDraft((d) => ({ ...d, to: v }))}
          />
        </label>
        <Button
          onClick={() => onRun(draft)}
          disabled={!draft.from || !draft.to || draft.from > draft.to}
        >
          Run report
        </Button>
        <div className="ml-auto flex flex-wrap items-center gap-1">
          {onExport ? (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Export to Excel (CSV)"
              title="Export to Excel (CSV)"
              onClick={onExport}
            >
              <Download className="size-4" />
            </Button>
          ) : null}
          <Button variant="ghost" size="icon" aria-label="Print" onClick={() => window.print()}>
            <Printer className="size-4" />
          </Button>
        </div>
      </div>
      {note}
      <div className="mx-auto max-w-6xl rounded-lg border bg-card px-8 py-6">
        <header className="mb-6 text-center">
          <div className="text-lg font-semibold">{orgName}</div>
          <div className="text-base">{title}</div>
          <div className="text-sm text-muted-foreground">
            {displayDate(from)} – {displayDate(to)}
            {trn ? ` · TRN ${trn}` : ''}
          </div>
        </header>
        {children}
        <footer className="mt-8 text-center text-xs text-muted-foreground">
          Accrual basis · Posted transactions with a VAT code
          {generatedAt
            ? ` · Generated ${new Date(generatedAt).toLocaleString('en-GB', { hour12: false })}`
            : ''}
        </footer>
      </div>
    </>
  );
}

/** Report search params: a from–to range, defaulting to last quarter. */
export function rangeSearch(s: Record<string, unknown>): { from: string; to: string } {
  const fallback = vatPresetRange('last-quarter', todayIso())!;
  const ok = (v: unknown) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
  return {
    from: ok(s['from']) ? (s['from'] as string) : fallback.from,
    to: ok(s['to']) ? (s['to'] as string) : fallback.to,
  };
}
